import { useLang } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MapPin,
  Bridge,
  UsersThree,
  TreeStructure,
  Globe,
  Gear,
  Scales,
  ArrowRight,
} from "@phosphor-icons/react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

const diffIcons = [MapPin, Bridge, UsersThree, TreeStructure, Globe, Gear, Scales];

export default function WhyGJC() {
  const { t } = useLang();

  return (
    <div data-testid="why-gjc-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#0D9488] mb-4"
          >
            {t.whyGJC.overline}
          </motion.p>
          <motion.h1
            initial="hidden"
            animate="visible"
            custom={1}
            variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#0A192F] max-w-4xl mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.whyGJC.headline}
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            custom={2}
            variants={fadeUp}
            className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl"
          >
            {t.whyGJC.intro}
          </motion.p>
        </div>
      </section>

      {/* Differentiators Grid */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="differentiators-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.whyGJC.differentiators.map((diff, i) => {
              const Icon = diffIcons[i];
              return (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                  variants={fadeUp}
                  className="bg-white border border-slate-200 p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                  data-testid={`differentiator-${i}`}
                >
                  <Icon size={36} weight="duotone" className="text-[#0D9488] mb-5" />
                  <h3
                    className="text-lg font-medium text-[#0A192F] mb-3"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {diff.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{diff.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A192F]" data-testid="why-gjc-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-3xl md:text-4xl font-medium tracking-tight text-white mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {t.contact.headline}
          </motion.h2>
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeUp}
            className="text-base md:text-lg text-slate-300 leading-relaxed max-w-xl mx-auto mb-8"
          >
            {t.contact.intro}
          </motion.p>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
            variants={fadeUp}
          >
            <Link
              to="/contact"
              data-testid="why-gjc-contact-cta"
              className="inline-flex items-center bg-[#0D9488] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#0F766E] transition-colors duration-300"
            >
              {t.nav.talkToUs} <ArrowRight size={18} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
